<template>
<div id="email-capture-component">
  <div v-if="hasImage">
    <img :src="image" />
  </div>

  <div
    class="tla-form-group"
    :class="{'tla-form-group--merged-top': hasImage}">
    <div id="email-capture-component">
      <div class="tla-form-group__wrapper">
        <input
          id="email-capture"
          autocomplete="email"
          type="email"
          v-validate.initial="`emailAddressVeeValidator`"
          data-vv-name="email-address"
          :data-vv-scope="validationScope"
          class="tla-form-group__input"
          :class="[{'tla-form-group__input--error': errors.first(`${validationScope}.email-capture`)},{'tla-form-group__input--merged-top': hasImage}]"
          v-model="emailAddress"
          name="email"
          spellcheck="false"
          :placeholder="emailAddressPlaceholder">
        <span v-if="showValidationSpinner" class="tla-form-group__wrapper--loading"></span>
      </div>
    </div>
    <span id="email-error" class="tla-form-group__message tla-form-group__message--error tla-form-group__message--spaced" v-if="errors.first(`${validationScope}.email-capture`)">
      {{ errors.first(`${validationScope}.email-capture`) }}
    </span>
  </div>
</div>
</template>

<script lang="ts">
import {
  Component,
  Prop,
  Vue,
  Watch,
  Inject
} from 'vue-property-decorator';
import { Validator } from 'vee-validate';
import Debounce from 'debounce-decorator';
import { namespace } from 'vuex-class';
import ExtraData from '../../models/ExtraData';
import { validationApiService } from '../../services/ValidationApiService';
import { validationService } from '../../services/ValidationService';
import emailValidationService from '../../services/EmailValidationService';
import RegexExpressions from '../../RegexExpressions';

const EnquiryModule = namespace('enquiryModule');

@Component
export default class EmailCaptureComponent extends Vue {
    @Inject('$validator') public $validator!: Validator;

    @EnquiryModule.Getter public currentStep: any;

    private nextStep : number = 0;

    @Prop({ default: '' })
    public mobileImage!: string;

    @Prop({ default: '' })
    public tabletImage!: string;

    @Prop({ default: '' })
    public desktopImage!: string;

    @Prop({ default: '' })
    public validationLevel!: string;

    @Prop({ default: 'Enter your email address' })
    public emailAddressPlaceholder!: string;

    @Prop({ default: null })
    public validationScope!: string;

    private emailAddress: string = '';

    private image: string = '';

    private showValidationSpinner: boolean = false;

    private isEmailAddressValid: boolean = false;

    public get hasImage() {
      return this.image && this.image !== '';
    }

    mounted() {
      this.nextStep = this.currentStep + 1;
      this.setImage();
      window.addEventListener('resize', this.setImage);

      Validator.extend('emailAddressVeeValidator', {
        getMessage: (field: string) => 'Please enter a valid email address or leave it blank',
        validate: async (emailAddress: string) => {
          if (emailAddress.length === 0 || this.useNoValidation()) {
            return true;
          }

          const simpleValidationResult = await validationApiService.isEmailValid(emailAddress);
          return simpleValidationResult;
        }
      });

      this.storeComponentDisplayedExtraData();
    }

    beforeDestroy() {
      window.removeEventListener('resize', this.setImage);
    }

    private setImage() {
      const screenWidth = window.innerWidth;

      if (screenWidth <= 768) {
        this.image = this.mobileImage;
      } else if (screenWidth <= 1024) {
        this.image = this.tabletImage;
      } else {
        this.image = this.desktopImage;
      }
    }

    @Debounce(700)
    @Watch('emailAddress')
    public async watchEmail() {
      await this.validateEmailAddress();
    }

    public async validateEmailAddress() {
      if (this.emailAddress.length === 0 || this.useNoValidation()) {
        this.setEmailAddressValidationStatus(true);
        return;
      }

      if (!validationApiService.isEmailAddressFormatValid(this.emailAddress)) {
        this.setEmailAddressValidationStatus(false);
        return;
      }

      this.showValidationSpinner = true;

      try {
        this.isEmailAddressValid = await validationApiService.isEmailValid(this.emailAddress, this.useCompleteValidation());
        this.setEmailAddressValidationStatus(this.isEmailAddressValid);
      } catch (error) {
        console.error('Error validating email address:', error);
        this.setEmailAddressValidationStatus(true);
      } finally {
        this.showValidationSpinner = false;
      }
    }

    private setEmailAddressValidationStatus(isValid: boolean) {
      this.isEmailAddressValid = isValid;
      if (isValid) {
        this.removeValidationErrorMessages();
      } else {
        this.showValidationErrorMessage();
      }
    }

    private showValidationErrorMessage() {
      this.$validator.errors.add({
        field: 'email-capture',
        msg: 'Please enter a valid email address',
        scope: this.validationScope,
        id: 'email-capture'
      });
    }

    private removeValidationErrorMessages() {
      this.$validator.errors.removeById('email-capture');
      this.$validator.errors.clear();
    }

    public getValidationClass(fieldName: string, fieldType?: string, fieldChanged?: boolean) {
      return validationService
        .getValidationClass(this.$validator, fieldName, fieldType, fieldChanged);
    }

    public get isNextClicked() {
      return validationService.hasNextBeenClicked(this.validationScope);
    }

    // If we're moving to the next step, and the email has passed simple validation
    // then kick off the full validation in the background
    @Watch('currentStep')
    public async watchIsNext() {
      if (this.currentStep !== this.nextStep) {
        return;
      }

      if (!this.emailAddress.length) {
        this.storeEmailNotEnteredExtraData();
        return;
      }

      if (!RegexExpressions.EMAIL.test(this.emailAddress)) {
        this.storeEmailNotEnteredExtraData();
        this.showValidationErrorMessage();
        return;
      }

      this.storeEmailEnteredExtraData();
      await emailValidationService.startCompleteEmailValidation(this.emailAddress);
    }

    private useCompleteValidation(): boolean {
      return this.validationLevel.toLowerCase() === 'complete';
    }

    private useNoValidation(): boolean {
      return this.validationLevel.toLowerCase() === 'none';
    }

    private storeComponentDisplayedExtraData() {
      const extraData: ExtraData = new ExtraData('emailCaptureComponentDisplayed', 'true');
      this.storeExtraData(extraData);
    }

    private storeEmailNotEnteredExtraData() {
      const extraData: ExtraData = new ExtraData('emailCaptureComponentEmailEntered', 'false');
      this.storeExtraData(extraData);
    }

    private storeEmailEnteredExtraData() {
      const extraData: ExtraData = new ExtraData('emailCaptureComponentEmailEntered', 'true');
      this.storeExtraData(extraData);
    }

    private storeExtraData(extraData: ExtraData) {
      this.$store.dispatch('enquiryModule/submitExtraData', extraData);
    }
}
</script>
